import type { Dispatch, MouseEvent, SetStateAction } from "react";
import type { TFunction } from "i18next";
import {
  type CandidateInfo,
  type CityHallStateResponse,
} from "../../client";
import PearlsBalance from "../../components/PearlsBalance";
import CandidateCard from "./CandidateCard";
import CandidateVoteButtons from "./CandidateVoteButtons";
import ElectionCountdownHeader from "./ElectionCountdownHeader";
import ElectionResultsNotice from "./ElectionResultsNotice";
import LeadingCandidateBanner from "./LeadingCandidateBanner";
import VoteListFooter from "./VoteListFooter";


type AdControllerRef = {
  current: {
    show: () => Promise<{ error?: boolean; done?: boolean }>;
    destroy?: () => void;
  } | null;
};

interface VoteListScreenProps {
  adControllerRef: AdControllerRef;
  adCooldowns: Record<number, number>;
  currentLeader: CandidateInfo | null;
  currentTime: Date;
  currentUserCandidateId: number | null;
  error: string | null;
  formatDate: (dateString: string | null | undefined) => string;
  formatNextElectionTimeRemaining: (nextElectionStartDate: string | null | undefined) => string;
  handleAdVote: (candidateId: number) => void;
  handleShowTooltip: (e: MouseEvent, candidate: CandidateInfo) => void;
  handleStarsVote: (candidateId: number) => Promise<void>;
  handleVoteClick: (candidateId: number) => void;
  isAdCooldown: (candidateId: number) => boolean;
  isLoading: boolean;
  isLoadingState: boolean;
  setCurrentScreen: (screen: "main" | "register") => void;
  setSelectedCandidateForHistory: Dispatch<SetStateAction<CandidateInfo | null>>;
  sortedCandidates: CandidateInfo[];
  starsVotingCandidateId: number | null;
  state: CityHallStateResponse | undefined;
  t: TFunction;
  votingCandidateId: number | null;
}

function VoteListScreen({
  adControllerRef,
  adCooldowns,
  currentLeader,
  currentTime,
  currentUserCandidateId,
  error,
  formatDate,
  formatNextElectionTimeRemaining,
  handleAdVote,
  handleShowTooltip,
  handleStarsVote,
  handleVoteClick,
  isAdCooldown,
  isLoading,
  isLoadingState,
  setCurrentScreen,
  setSelectedCandidateForHistory,
  sortedCandidates,
  starsVotingCandidateId,
  state,
  t,
  votingCandidateId,
}: VoteListScreenProps) {
  return (
    <div
      className="absolute top-[54px] bottom-0 left-1/2 z-[10004] flex w-full max-w-[349px] -translate-x-1/2 flex-col"
    >
      {/* Fixed "Currently Leading" card */}
      {currentLeader && (
        <LeadingCandidateBanner leader={currentLeader} t={t} />
      )}

      {/* Election countdown — segmented digital timer */}
      {state?.voting_available && state?.election_end_at && (
        <ElectionCountdownHeader
          electionEndAt={state.election_end_at}
          currentTime={currentTime}
          t={t}
        />
      )}

      {/* Balance */}
      <div className="mb-md shrink-0">
        <PearlsBalance variant="dark" compact hideCrypto />
      </div>

      {/* Scrollable candidate list */}
      <div
        className="cityhall-scrollable flex flex-1 flex-col gap-[10px] overflow-x-hidden overflow-y-auto pb-2xl"
      >
        {/* Election info */}
        {!state?.voting_available && state?.next_election_start_date && (
          <ElectionResultsNotice
            nextElectionStartDate={state.next_election_start_date}
            formatDate={formatDate}
            formatNextElectionTimeRemaining={formatNextElectionTimeRemaining}
            t={t}
          />
        )}

        {error && (
          <div className="rounded-[10px] border border-register-screens-border bg-register-screens-surface px-xl py-[10px] text-[14px] text-error-soft">
            {error}
          </div>
        )}

        {isLoadingState ? (
          <div className="p-3xl text-center [font-family:Lato,sans-serif] text-[18px] text-white">
            {t("Loading...")}
          </div>
        ) : sortedCandidates.length > 0 ? (
          // Pre-extract VoteListScreen already read adControllerRef.current during list
          // render; keep that gate, suppress the same react-hooks/refs diagnostic.
          // eslint-disable-next-line react-hooks/refs -- ad-controller presence gate (pre-existing)
          sortedCandidates.map((candidate, index) => {
            const isLeader = currentLeader?.id === candidate.id;
            return (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                index={index}
                isLeader={isLeader}
                onShowTooltip={handleShowTooltip}
                onShowHistory={setSelectedCandidateForHistory}
                t={t}
              >
                {state?.voting_available && (
                  <CandidateVoteButtons
                    candidateId={candidate.id}
                    votePricePearls={state.vote_price_pearls}
                    adControllerAvailable={adControllerRef.current != null}
                    adCooldowns={adCooldowns}
                    isAdCooldown={isAdCooldown}
                    onPearlVote={handleVoteClick}
                    onAdVote={handleAdVote}
                    onStarsVote={handleStarsVote}
                    starsVotingCandidateId={starsVotingCandidateId}
                    votingCandidateId={votingCandidateId}
                  />
                )}
              </CandidateCard>
            );
          })
        ) : (
          <div className="p-3xl text-center [font-family:Lato,sans-serif] text-[18px] text-white">
            {t("No candidates available")}
          </div>
        )}

        <VoteListFooter
          showBecomeMayor={!!state?.registration_available && !currentUserCandidateId}
          isLoading={isLoading}
          onBecomeMayor={() => setCurrentScreen("register")}
          onBack={() => setCurrentScreen("main")}
          t={t}
        />
      </div>
    </div>
  );
}

export default VoteListScreen;
